import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled, { css } from '@mint-lib/styled';
import { equals } from 'remeda';

import { Box, IconButton, Input, Popover } from '@myn/mui';

import { BUILDER_ROW_TYPES } from '../../../../../../constants/campaignBuilder.js';
import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';
import TableEditCell from '../TableEditCell/TableEditCell.jsx';
import DaySelectModal from './DaySelectModal.jsx';

type Props = {
  value: string[] | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
  cellColor: string;
};

const DaySelectField = ({ row, column, value, cellColor }: Props) => {
  const [selectedValues, setSelectedValues] = useState<string[] | null>(value);

  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const { options, onlyTileEditable } = column;
  const {
    id: rowId,
    type,
    isBuyingPlanLocked,
    isCampaignCurrencyMode,
    isPredictivePlanningMode,
    isArchivedVersion,
    scale,
  } = row.original;

  const isTotalRow = type === BUILDER_ROW_TYPES.TOTAL;
  const isDisabled = type !== BUILDER_ROW_TYPES.TILE && !!onlyTileEditable;

  const areAllSelected = selectedValues?.length === options?.length;

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const isReadOnlyField =
    isPredictivePlanningMode ||
    isArchivedVersion ||
    !isAllowed ||
    isCampaignCurrencyMode ||
    isBuyingPlanLocked;

  const { startMeasurePerformance } = column;

  useEffect(() => {
    if (isFocused && inputWrapperRef.current) {
      setAnchorEl(inputWrapperRef.current);
    }
  }, [isFocused]);

  const handleEditButtonClick = () => {
    setIsFocused(true);
  };

  const handleCloseModal = () => {
    setIsFocused(false);
    setAnchorEl(null);
  };
  const handleApply = useCallback(() => {
    const preparedValue = selectedValues?.length ? selectedValues : null;
    const isSameValue = equals(value, preparedValue);
    if (!isSameValue) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);
      column.updateRow(
        {
          id: rowId,
        },
        column.id,
        preparedValue,
      );
    }

    handleCloseModal();
  }, [value, selectedValues, row, column]);

  const handleClearSelect = (event: MouseEvent) => {
    event.stopPropagation();

    if (isAllowed && !isPredictivePlanningMode) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);
      column.updateRow(
        {
          id: rowId,
        },
        column.id,
        null,
      );
    }
  };

  const handleSelectAllDays = useCallback(() => {
    if (areAllSelected) {
      setSelectedValues([]);
      return;
    }

    setSelectedValues(options?.map((day) => day.value));
  }, [selectedValues, options]);

  const handleChangeDay = useCallback(
    (dayValue: string) => {
      if (!selectedValues) {
        return setSelectedValues([dayValue]);
      }

      const isDaySelected = selectedValues.includes(dayValue);
      if (isDaySelected) {
        setSelectedValues(
          selectedValues.filter((selectedValue) => selectedValue !== dayValue),
        );
        return;
      }

      setSelectedValues([...selectedValues, dayValue]);
    },
    [selectedValues],
  );

  const blockTitle = useMemo(() => {
    return options
      ?.map((option) => {
        const isDaySelected = selectedValues?.includes(option.value);
        if (isDaySelected) {
          return option.displayName.slice(0, 1).toUpperCase();
        }

        return '-';
      })
      .join('');
  }, [value, options]);

  const displayValue = value ? blockTitle : null;

  if (isTotalRow || isDisabled) {
    return null;
  }

  if (isReadOnlyField) {
    return (
      <FormattedField
        scale={scale}
        value={displayValue}
        color={cellColor}
        variant="text"
      />
    );
  }

  if (!isFocused) {
    return (
      <TableEditCell
        scale={scale}
        isDeleteButtonShown={!!value}
        deleteValueFunc={handleClearSelect}
        showEditModeFunc={handleEditButtonClick}
        startMeasurePerformance={startMeasurePerformance}
        value={displayValue}
        variant="text"
      />
    );
  }

  return (
    <Wrapper>
      <InputWrapper ref={inputWrapperRef}>
        <StyledInput
          type="text"
          value={displayValue || ''}
          onClick={handleEditButtonClick}
          placeholder={'Select...'}
          $scale={scale}
          endAdornment={
            <IconButton
              variant="icon"
              icon="ChevronUp"
              onClick={handleEditButtonClick}
              sx={{
                zoom: scale,
              }}
            />
          }
        />
      </InputWrapper>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleCloseModal}
      >
        <DaySelectModal
          options={options}
          onClickSelectAll={handleSelectAllDays}
          areAllSelected={areAllSelected}
          selectedValues={selectedValues}
          onChangeDay={handleChangeDay}
          handleApply={handleApply}
          handleCloseModal={handleCloseModal}
        />
      </Popover>
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  width: 100%;
  height: 100%;
`;

const InputWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  & > div {
    height: 100%;
  }
`;

const StyledInput = styled(Input)<{
  $scale: number;
}>`
  min-height: 100%;
  width: 100%;

  ${({ $scale }) => css`
    font-size: ${0.875 * $scale}rem;
    line-height: ${1.25 * $scale};
  `}

  input {
    caret-color: transparent;
  }

  box-shadow: ${({ theme }) =>
    `inset 0 0 0 1px ${theme.palette.action.focusBorder}`};
`;

export default DaySelectField;
